import React, { useState, useEffect, useRef } from 'react';
import styles from '../../../Dashboard/index.module.less';
import {
  Button,
  Col,
  Row,
  Typography,
  DatePicker,
  Card,
  Space,
  Avatar,
  Tag,
  Badge,
  Empty,
  Select,
  Tooltip
} from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  // CaretRightOutlined,
  WarningOutlined,
  // CheckOutlined,
  ClockCircleOutlined,
  DownOutlined,
  UpOutlined,
  UnorderedListOutlined,
  CheckOutlined,
  FlagFilled,
  RollbackOutlined,
  EyeOutlined,
  EyeFilled,
  FilterOutlined
  //   CaretDownOutlined,
  //   CaretUpOutlined
} from '@ant-design/icons';
import CustomButton from '@/common/CustomButton';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllEmptasks,
  selectReviewSlice,
  updateRelease,
  watchTask
} from '../../redux/slice';
import { faArrowsToEye, faRedo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CountCard from '../CountCard';
import moment from 'moment';
import ColorHash from 'color-hash';
import { getLoggedInUser } from 'utilities/helpers';
import { EXECUTIVE, MANAGER } from 'utilities/roleRights';
import FloatingActionButton from 'pages/FloatingActionButton';
import watchers from '../../../../assets/icons/watchers.svg';
import { getAllUsers } from 'pages/Tasks/redux/slice';

const { Text, Title } = Typography;
const colorHash = new ColorHash({ lightness: 0.7 });
const colorHasher = new ColorHash({ lightness: 0.5 });

// Returns today, or the previous Friday if today is Saturday or Sunday
const getEffectiveToday = () => {
  const d = new Date();
  const day = d.getDay(); // 0 = Sunday, 6 = Saturday
  if (day === 0) d.setDate(d.getDate() - 2);
  else if (day === 6) d.setDate(d.getDate() - 1);
  return d;
};

const TaskTracker = () => {
  // Use Date object; if today is Sat/Sun, start at Friday
  const [currentDate, setCurrentDate] = useState(getEffectiveToday);
  const currentDateRef = useRef(currentDate);
  currentDateRef.current = currentDate;
  const lastFetchedPlanDateRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [totalTasks, setTotalTasks] = useState(0);
  const [completedTasks, setCompletedTasks] = useState(0);
  const [remainingTasks, setRemainingTasks] = useState(0);
  const [escalatedTasksCount, setEscalatedTasksCount] = useState(0);
  const [releasedTasksCount, setReleasedTasksCount] = useState(0);
  const [escalatedTasksVisible, setEscalatedTasksVisible] = useState(true);
  const [pendingTasksVisible, setPendingTasksVisible] = useState([]);
  const [focusTasksVisible, setFocusTasksVisible] = useState([]);
  const [groupedByUser, setGroupedByUser] = useState([]);
  const [groupedByUserFocus, setGroupedByUserFocus] = useState([]);
  const [escalatedTasks, setEscalatedTasks] = useState([]);
  const [taskData, setTaskData] = useState([]);
  const [users, setUsers] = useState([]);
  const [idToReleaseLoading, setIdToReleaseLoading] = useState(null);
  const [showOnlyPendingTask, setShowOnlyPendingTask] = useState(false);
  const [showMyFocus, setShowMyFocus] = useState(false);
  const [watchedTasks, setWatchedTasks] = useState({});

  const [showReleasedOnly, setShowReleasedOnly] = useState(false);
  const [groupedByUserReleased, setGroupedByUserReleased] = useState([]);
  const [releasedTasksVisible, setReleasedTasksVisible] = useState([]);
  const [openWatch, setOpenWatch] = useState(false);
  const [fullTeam, setFullTeam] = useState([]);

  const { loading } = useSelector(selectReviewSlice);
  const user = getLoggedInUser();

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getAllUsers()).then((res) => {
      if (res?.payload?.success) {
        setFullTeam(res.payload.data);
      }
    });
  }, [dispatch]);

  // different approach
  const toggleOption = () => {
    if (loading) return;

    setShowOnlyPendingTask((prev) => {
      const newVal = !prev;
      setShowMyFocus(false);
      setShowReleasedOnly(false);
      return newVal;
    });
  };

  const toggleMyFocusOption = () => {
    if (loading) return;

    setShowMyFocus((prev) => {
      const newVal = !prev;
      setShowOnlyPendingTask(false);
      setShowReleasedOnly(false);
      return newVal;
    });
  };

  const toggleReleasedOption = () => {
    if (loading) return;

    setShowReleasedOnly((prev) => {
      const newVal = !prev;
      setShowOnlyPendingTask(false);
      setShowMyFocus(false);
      return newVal;
    });
  };

  useEffect(() => {
    handleGetAllTasks();
  }, [showOnlyPendingTask, showMyFocus, showReleasedOnly, currentDate]);

  const handleGetAllTasks = (user_id = '') => {
    const planDate = moment(currentDate).format('YYYY-MM-DD');
    const isDateChange = lastFetchedPlanDateRef.current !== planDate;
    if (isDateChange) {
      lastFetchedPlanDateRef.current = planDate;
      setTaskData([]);
      setTotalTasks(0);
      setCompletedTasks(0);
      setRemainingTasks(0);
      setEscalatedTasksCount(0);
      setReleasedTasksCount(0);
    }

    const payload = {
      user_id,
      plan_date: planDate
    };

    //added just now
    if (showOnlyPendingTask) {
      payload.status = 'pending';
    } else if (showMyFocus) {
      payload.my_focus = 'true';
    } else if (showReleasedOnly) {
      payload.released_tasks_only = 'true'; // Add this condition
    }
    // If neither is true, don't set status (gets all tasks)

    dispatch(getAllEmptasks(payload)).then((res) => {
      // Ignore stale responses: only update if we're still on the same date
      const selectedDate = moment(currentDateRef.current).format('YYYY-MM-DD');
      if (selectedDate !== planDate) return;

      if (res?.payload?.success) {
        const data = res.payload.data;
        setTaskData(Array.isArray(data) ? data : []);
        setTotalTasks(res?.payload?.task_statistics?.total_tasks ?? 0);
        setCompletedTasks(res?.payload?.task_statistics?.completed_tasks ?? 0);
        setRemainingTasks(res?.payload?.task_statistics?.remaining_tasks ?? 0);
        setEscalatedTasksCount(
          res?.payload?.task_statistics?.escalated_tasks ?? 0
        );
        setReleasedTasksCount(
          res?.payload?.task_statistics?.released_tasks ?? 0
        );
      }
    });
  };

  // const organizeTaskData = (data) => {
  //   // Separate escalated tasks
  //   const escalatedTasks = data.filter(
  //     (item) => item.task && item.task_status === 'escalated'
  //   );

  //   // Group remaining tasks by user
  //   let remainingTasks;

  //   if (showReleasedOnly) {
  //     // For released tasks, filter by released status
  //     remainingTasks = data.filter(
  //       (item) =>
  //         item.task &&
  //         item.task_status !== 'escalated' &&
  //         (item.task_status === 'released' ||
  //           item.status === 'released' ||
  //           item.is_released === true)
  //       // Adjust the condition based on how your API returns released tasks
  //     );
  //   } else {
  //     remainingTasks = data.filter(
  //       (item) => item.task && item.task_status !== 'escalated'
  //     );
  //   }

  //   const groupedByUser = {};
  //   const userMap = new Map();

  //   remainingTasks.forEach((task) => {
  //     const userId = task.user_id;
  //     const userName = task.user
  //       ? `${task.user.first_name} ${task.user.last_name}`
  //       : `User ${userId}`;

  //     if (task.user && !userMap.has(task.user.id)) {
  //       userMap.set(task.user.id, task.user);
  //     }

  //     if (!groupedByUser[userId]) {
  //       groupedByUser[userId] = {
  //         userName,
  //         tasks: []
  //       };
  //     }

  //     groupedByUser[userId].tasks.push(task);
  //   });

  //   return {
  //     escalatedTasks,
  //     groupedByUser,
  //     users: Array.from(userMap.values())
  //   };
  // };
  const organizeTaskData = (data) => {
    // Separate escalated tasks
    const escalatedTasks = data.filter(
      (item) => item.task && item.task_status === 'escalated'
    );

    let remainingTasks;

    if (showReleasedOnly) {
      // Fixed: Check for released tasks using the correct properties
      remainingTasks = data.filter((item) => {
        if (!item.task || item.task_status === 'escalated') return false;

        // Check multiple possible fields for released status based on your data structure
        return (
          item.plan_released === true ||
          item.task?.is_released === true ||
          item.task?.task_released === true
        );
      });
    } else {
      remainingTasks = data.filter(
        (item) => item.task && item.task_status !== 'escalated'
      );
    }

    const groupedByUser = {};
    const userMap = new Map();

    remainingTasks.forEach((task) => {
      const userId = task.user_id;
      const userName = task.user
        ? `${task.user.first_name} ${task.user.last_name}`
        : `User ${userId}`;

      if (task.user && !userMap.has(task.user.id)) {
        userMap.set(task.user.id, task.user);
      }

      if (!groupedByUser[userId]) {
        groupedByUser[userId] = {
          userName,
          role_id: task.user?.role_id ?? null,
          staff_augmentation_percentage:
            task.user?.staff_augmentation_percentage ?? 0,
          tasks: []
        };
      }

      groupedByUser[userId].tasks.push(task);
    });

    console.log('remainingTasks', remainingTasks);

    return {
      escalatedTasks,
      groupedByUser,
      users: Array.from(userMap.values())
    };
  };
  useEffect(() => {
    if (taskData && taskData.length > 0) {
      const { escalatedTasks, groupedByUser, users } =
        organizeTaskData(taskData);

      if (showMyFocus) {
        setGroupedByUserFocus(groupedByUser);
        setFocusTasksVisible(Object.keys(groupedByUser));
        setGroupedByUser({});
        setGroupedByUserReleased({}); // Clear released
        setPendingTasksVisible([]);
        setReleasedTasksVisible([]); // Clear released visibility
      } else if (showReleasedOnly) {
        // Add this condition
        setGroupedByUserReleased(groupedByUser);
        setReleasedTasksVisible(Object.keys(groupedByUser));
        setGroupedByUser({});
        setGroupedByUserFocus({});
        setPendingTasksVisible([]);
        setFocusTasksVisible([]);
      } else {
        setGroupedByUser(groupedByUser);
        setPendingTasksVisible(Object.keys(groupedByUser));
        setGroupedByUserFocus({});
        setGroupedByUserReleased({}); // Clear released
        setFocusTasksVisible([]);
        setReleasedTasksVisible([]); // Clear released visibility
      }

      setEscalatedTasks(escalatedTasks);
      setUsers(users);
    } else {
      // Clear derived state when taskData is empty so UI updates correctly
      setGroupedByUser({});
      setGroupedByUserFocus({});
      setGroupedByUserReleased({});
      setPendingTasksVisible([]);
      setFocusTasksVisible([]);
      setReleasedTasksVisible([]);
      setEscalatedTasks([]);
      setUsers([]);
    }
  }, [taskData, showMyFocus, showReleasedOnly]); // Add showReleasedOnly dependency
  const formattedDate = currentDate.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  const resetFiltersOnDateChange = () => {
    setShowOnlyPendingTask(false);
    setShowMyFocus(false);
    setShowReleasedOnly(false);
  };

  const handleDateChange = (date) => {
    if (date) {
      resetFiltersOnDateChange();
      setCurrentDate(date.toDate());
    }
  };

  // Go to previous weekday (skip Saturday and Sunday)
  const goToPreviousDay = () => {
    resetFiltersOnDateChange();
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 1);
    while (newDate.getDay() === 0 || newDate.getDay() === 6) {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  // Go to next weekday (skip Saturday and Sunday); do not go past effective today
  const goToNextDay = () => {
    resetFiltersOnDateChange();
    const nextDate = new Date(currentDate);
    nextDate.setDate(currentDate.getDate() + 1);
    while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
      nextDate.setDate(nextDate.getDate() + 1);
    }
    const effectiveToday = getEffectiveToday();
    effectiveToday.setHours(0, 0, 0, 0);
    nextDate.setHours(0, 0, 0, 0);
    if (nextDate <= effectiveToday) setCurrentDate(nextDate);
  };

  const effectiveToday = getEffectiveToday();
  const isCurrentDateToday = moment(currentDate).isSame(
    moment(effectiveToday),
    'day'
  );

  // Disable weekends (Sat/Sun) and dates after effective today
  const disabledDate = (current) => {
    if (!current) return false;
    const day = moment(current).day(); // 0 = Sunday, 6 = Saturday
    if (day === 0 || day === 6) return true;
    return moment(current).isAfter(moment(effectiveToday), 'day');
  };

  // Toggle pending tasks visibility
  const togglePendingTasks = (index) => {
    setPendingTasksVisible((prevKey) => {
      if (prevKey.includes(index)) {
        return prevKey.filter((key) => key !== index);
      } else {
        return [...prevKey, index];
      }
    });
  };

  // Toggle focus tasks visibility
  const toggleFocusTasks = (index) => {
    setFocusTasksVisible((prevKey) => {
      if (prevKey.includes(index)) {
        return prevKey.filter((key) => key !== index);
      } else {
        return [...prevKey, index];
      }
    });
  };

  //added just now
  const toggleReleasedTasks = (index) => {
    setReleasedTasksVisible((prevKey) => {
      if (prevKey.includes(index)) {
        return prevKey.filter((key) => key !== index);
      } else {
        return [...prevKey, index];
      }
    });
  };

  const toggleescalatedTasks = () => {
    setEscalatedTasksVisible(!escalatedTasksVisible);
  };

  const handleRelease = (plan_id) => {
    const payload = { plan_id };
    setIdToReleaseLoading(plan_id);
    dispatch(updateRelease(payload)).then((res) => {
      if (res?.payload?.success) {
        setIdToReleaseLoading(null);
        setGroupedByUser({});
        setGroupedByUserFocus({});
        setEscalatedTasks([]);
        handleGetAllTasks();
      } else {
        setIdToReleaseLoading(null);
      }
    });
  };

  const handleWatch = (taskId, is_watching) => {
    dispatch(
      watchTask({
        task_id: taskId,
        is_watching: !is_watching
      })
    ).then((res) => {
      if (res?.payload?.success) {
        handleGetAllTasks();
      }
    });
  };

  useEffect(() => {
    // console.log("Updated watched tasks:", watchedTasks);
  }, [watchedTasks]);

  const Metrics = [
    {
      label: 'Total Tasks',
      count: totalTasks,
      icon: <UnorderedListOutlined />,
      iconBg: '#e6f7ff',
      iconColor: 'rgb(76, 0, 255)',
      countColor: '#000000'
    },
    {
      label: 'Completed',
      count: completedTasks,
      icon: <CheckOutlined />,
      iconBg: '#d9f7be',
      iconColor: '#52c41a',
      countColor: '#52c41a'
    },
    {
      label: 'In Progress',
      count: remainingTasks,
      icon: <ClockCircleOutlined />,
      iconBg: '#fff7e6',
      iconColor: '#fa8c16',
      countColor: '#1890ff'
    },
    {
      label: 'Escalated Tasks',
      count: escalatedTasksCount,
      icon: <FlagFilled />,
      iconBg: '#fff1f0',
      iconColor: '#ff4d4f',
      countColor: '#ff4d4f'
    },
    {
      label: 'Released',
      count: releasedTasksCount,
      icon: (
        <FontAwesomeIcon icon={faRedo} style={{ transform: 'scaleX(-1)' }} />
      ),
      iconBg: '#fef9c3',
      iconColor: '#ca8a04',
      countColor: '#ca8a04'
    }
  ];

  const storedData = localStorage.getItem('trackex_user');
  const parsedData = JSON.parse(storedData);
  const userId = parsedData?.user_details?.id;
  console.log('User ID:', userId);

  console.log('groupedByUser', groupedByUser);

  const validUsers = Object.values(groupedByUser).filter(
    (user) =>
      user.role_id !== 3 &&
      Number(user.staff_augmentation_percentage || 0) !== 100
  );
  const AllActiveUsers = fullTeam.filter(
    (member) =>
      // !member.available &&
      !member.on_leave &&
      member.staff_augmentation_percentage !== 100 &&
      member.role_id !== 3
  );
  const avgUtilizationPercentage =
    validUsers.length > 0
      ? (validUsers.reduce((total, user) => {
          const userHours = user.tasks.reduce(
            (sum, task) => sum + Number(task.task?.hours || 0),
            0
          );

          const divisor =
            Number(user.staff_augmentation_percentage) === 50 ? 4 : 8;

          return total + userHours / divisor;
        }, 0) /
          AllActiveUsers.length) *
        100
      : 0;

  return (
    <>
      <div className="flex items-center justify-between">
        <div className={'flex items-center'}>
          <Button
            type="text"
            icon={<LeftOutlined />}
            className={'!text-sm md:!text-[16px]  font-bold !m-0'}
            onClick={goToPreviousDay}
          />
          <Text className={'!text-sm md:!text-[16px]  font-bold !m-0'}>
            {formattedDate}
          </Text>
          <Button
            type="text"
            icon={<RightOutlined />}
            className={'!text-sm md:!text-[16px]  font-bold !m-0'}
            onClick={goToNextDay}
            disabled={isCurrentDateToday}
          />
        </div>
        <div className="flex">
          <DatePicker
            value={moment(currentDate)}
            onChange={handleDateChange}
            placeholder="Select Date"
            disabledDate={disabledDate}
          />
        </div>
      </div>
      <div
        className={
          'mt-3 grid-cols-1 grid sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-6'
        }
      >
        {Metrics.map((card) => {
          return <CountCard key={card.label} {...card} />;
        })}
      </div>
      <br />

      {/* escalated tasks */}
      <Row gutter={24} className={styles.RowStyles}>
        <Col span={24}>
          {escalatedTasks?.length ? (
            <Card className={styles.taskCard} bordered={false}>
              <Row gutter={24}>
                <Col span={20}>
                  <Title
                    level={5}
                    className={styles.taskTitle}
                    style={{ margin: 0 }}
                  >
                    <Space>
                      {<WarningOutlined className={styles.stuckIcon} />}
                      {`Escalated Tasks`}
                    </Space>
                  </Title>
                </Col>
                <Col span={4} style={{ textAlign: 'right' }}>
                  <Space align="center" size={4} className={styles.caret1}>
                    <Badge
                      count={escalatedTasks?.length}
                      className={styles.WarningBadgeIconStyle}
                    />
                    {escalatedTasksVisible ? (
                      <DownOutlined onClick={toggleescalatedTasks} />
                    ) : (
                      <UpOutlined onClick={toggleescalatedTasks} />
                    )}
                  </Space>
                </Col>
              </Row>
              {escalatedTasksVisible &&
                escalatedTasks?.map((item, index) => {
                  return (
                    <Card
                      className={styles.escalated_nestedCard}
                      bordered={false}
                      key={index}
                    >
                      <Row gutter={24}>
                        <Col span={22}>
                          <Title
                            level={5}
                            className={styles.taskTitle}
                            style={{ margin: 0 }}
                          >
                            <Space size={2}>
                              {item?.task?.title}{' '}
                              <span
                                style={{ fontSize: '8px', color: '#64748B' }}
                              >
                                Escalated by{' '}
                                {`${item?.user?.first_name} ${item?.user?.last_name}`}{' '}
                                {item?.task?.escalated_date ? (
                                  <>
                                    at{' '}
                                    {moment(item.task.escalated_date)
                                      .utcOffset('+05:30')
                                      .format('hh:mm A')}
                                  </>
                                ) : null}
                              </span>
                            </Space>
                          </Title>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col span={24}>
                          <Text
                            type="secondary"
                            className={styles.descriptionText}
                          >
                            {item?.task?.description}
                          </Text>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col span={24}>
                          <Text
                            type="secondary"
                            className={styles.secondaryText}
                          >
                            {item?.task?.client?.client_name} ·{' '}
                            {item?.task?.project?.project} ·{' '}
                            {`${item.assigner?.first_name} ${item.assigner?.last_name}`}{' '}
                            · {`T${item.task_id}`}
                          </Text>
                        </Col>
                      </Row>
                      <Row gutter={24}>
                        <Col span={24}>
                          <Text
                            type="secondary"
                            className={styles.secondaryText}
                          >
                            Reason: {item.stuck_comments}
                          </Text>
                        </Col>
                      </Row>
                      <Row gutter={24} className={styles.RowStyles}>
                        {/* <Col
                          span={24}
                          hidden={user.user_details.role_id !== MANAGER}
                        >
                          <CustomButton
                            className={styles.releaseButton}
                            size="small"
                            onClick={() => handleRelease(item?.id)}
                            icon={<RollbackOutlined />}
                            loading={idToReleaseLoading === item?.id}
                          >
                            Release
                          </CustomButton>
                        </Col> */}
                        <Col
                          span={24}
                          hidden={
                            !(
                              user.user_details.role_id === MANAGER ||
                              user.user_details.role_id === EXECUTIVE
                            )
                          }
                        >
                          <CustomButton
                            className={styles.releaseButton}
                            size="small"
                            onClick={() => handleRelease(item?.id)}
                            icon={<RollbackOutlined />}
                            loading={idToReleaseLoading === item?.id}
                          >
                            Release
                          </CustomButton>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
            </Card>
          ) : (
            ''
          )}
        </Col>
      </Row>

      <Row gutter={24} className={styles.RowStyles}>
        <Col span={24}>
          {/* <div className="gap-2 !items-center grid grid-cols-1 sm:grid-cols-[4fr_1fr] ">
            <div>
              <Title
                level={5}
                className={styles.taskTitle}
                style={{ margin: 0 }}
              >
                <Space
                  className={'!text-xl md:!text-3xl !font-bold md:mt-0 mt-2'}
                >
                  {`Team Tasks & Progress Tracker`}
                </Space>
              </Title>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <div>Total Utilization:</div>
                <Text strong>{avgUtilizationPercentage.toFixed(2)}%</Text>
              </div>
              <CustomButton
                type="primary"
                style={{
                  border: '1px solid #dddddd',
                  backgroundColor: showReleasedOnly ? '#0653fa' : 'white', // Update condition
                  color: showReleasedOnly ? 'white' : 'black' // Update condition
                }}
                className={'!select-none !transition-all !duration-300'}
                onClick={toggleReleasedOption} // Update onClick
              >
                <RollbackOutlined /> Released
              </CustomButton>
              <CustomButton
                type="primary"
                style={{
                  border: '1px solid #dddddd',
                  backgroundColor: showMyFocus ? '#0653fa' : 'white',
                  color: showMyFocus ? 'white' : 'black'
                }}
                className={'!select-none !transition-all !duration-300'}
                onClick={toggleMyFocusOption}
              >
                <FilterOutlined /> My Focus
              </CustomButton>

              <CustomButton
                type="primary"
                style={{
                  border: '1px solid #dddddd',
                  backgroundColor: showOnlyPendingTask ? '#0653fa' : 'white',
                  color: showOnlyPendingTask ? 'white' : 'black'
                }}
                className={'!select-none !transition-all !duration-300'}
                onClick={toggleOption}
              >
                <ClockCircleOutlined /> Pending Tasks
              </CustomButton>

              <Select
                showSearch
                placeholder="Select member"
                optionFilterProp="children"
                defaultValue="Select member"
                style={{ width: '100%' }}
                className={styles.selectField}
                onChange={handleGetAllTasks}
                allowClear
              >
                {users?.length
                  ? users?.map((item, index) => {
                      return (
                        <option value={item?.id} key={index}>
                          {item?.first_name} {item?.last_name}
                        </option>
                      );
                    })
                  : ''}
              </Select>
            </div>
          </div> */}
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between">
            {/* Left: Title */}
            <div>
              <Title
                level={5}
                className={styles.taskTitle}
                style={{ margin: 0 }}
              >
                <Space className="!text-xl md:!text-3xl !font-bold md:mt-0 mt-2">
                  Team Tasks & Progress Tracker
                </Space>
              </Title>
            </div>

            {/* Right: Controls */}
            <div className="flex flex-wrap items-center gap-2">
              {user?.user_details?.role_id === EXECUTIVE && (
                <div className="flex items-center gap-2">
                  <div>Total Utilization:</div>
                  <div>{avgUtilizationPercentage.toFixed(2)}%</div>
                </div>
              )}

              <CustomButton
                type="primary"
                style={{
                  border: '1px solid #dddddd',
                  backgroundColor: showReleasedOnly ? '#0653fa' : 'white',
                  color: showReleasedOnly ? 'white' : 'black'
                }}
                className="!select-none !transition-all !duration-300"
                onClick={toggleReleasedOption}
              >
                <RollbackOutlined /> Released
              </CustomButton>

              <CustomButton
                type="primary"
                style={{
                  border: '1px solid #dddddd',
                  backgroundColor: showMyFocus ? '#0653fa' : 'white',
                  color: showMyFocus ? 'white' : 'black'
                }}
                className="!select-none !transition-all !duration-300"
                onClick={toggleMyFocusOption}
              >
                <FilterOutlined /> My Focus
              </CustomButton>

              <CustomButton
                type="primary"
                style={{
                  border: '1px solid #dddddd',
                  backgroundColor: showOnlyPendingTask ? '#0653fa' : 'white',
                  color: showOnlyPendingTask ? 'white' : 'black'
                }}
                className="!select-none !transition-all !duration-300"
                onClick={toggleOption}
              >
                <ClockCircleOutlined /> Pending Tasks
              </CustomButton>

              <Select
                showSearch
                placeholder="Select member"
                optionFilterProp="children"
                style={{ minWidth: 160 }}
                className={styles.selectField}
                onChange={handleGetAllTasks}
                allowClear
              >
                {users?.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.first_name} {item.last_name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* grouped by user */}
          {Object.keys(groupedByUser).length > 0
            ? Object.entries(groupedByUser).map(
                ([key, { userName, tasks, index }]) => {
                  const totalHours = tasks?.reduce((sum, item) => {
                    return sum + Number(item?.task?.hours || 0);
                  }, 0);
                  return (
                    <Card
                      className={`${styles.taskCard} mb-6`}
                      bordered={false}
                      key={index}
                    >
                      <Row
                        align="middle"
                        style={{
                          marginBottom: '16px',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Col span={20}>
                          <Space>
                            <Avatar
                              style={{
                                backgroundColor: colorHash.hex(
                                  userName
                                    .split(' ')
                                    .map((word) => word[0])
                                    .join('')
                                )
                              }}
                            >
                              {userName
                                .split(' ')
                                .map((word) => word[0])
                                .join('')}
                            </Avatar>
                            <Text strong>{userName}</Text>
                          </Space>
                        </Col>
                        <Row style={{ gap: 10 }}>
                          {user?.user_details?.role_id === EXECUTIVE && (
                            <Row span={8} style={{ textAlign: 'right' }}>
                              <Text
                                type="secondary"
                                className={styles.taskCount}
                              >
                                <ClockCircleOutlined /> {` `}
                                {totalHours} hrs
                              </Text>
                            </Row>
                          )}
                          <Row span={4} style={{ textAlign: 'right' }}>
                            <Space
                              align="center"
                              size={4}
                              className={styles.caret}
                            >
                              <Badge
                                count={tasks?.length}
                                className={styles.BadgeIconStyle}
                              />
                              {!pendingTasksVisible.includes(key) ? (
                                <DownOutlined
                                  onClick={() => togglePendingTasks(key)}
                                />
                              ) : (
                                <UpOutlined
                                  onClick={() => togglePendingTasks(key)}
                                />
                              )}
                            </Space>
                          </Row>
                        </Row>
                      </Row>
                      {pendingTasksVisible.includes(key) &&
                        tasks?.map((item, index) => {
                          const isAdHOc = item.task.task_type === 'ad_hoc';
                          const taskHours = Number(item?.task?.hours || 0);

                          return (
                            <div
                              className={'!mt-1.5 bg-[#f9fafb] !rounded-lg'}
                              key={index}
                            >
                              <div className="flex items-center justify-between gap-6 px-3 pt-2.5">
                                <Title
                                  level={5}
                                  className={styles.taskTitle}
                                  style={{ margin: 0 }}
                                >
                                  {item?.task?.title}
                                </Title>
                                <div className="flex items-center gap-2">
                                  {user?.user_details?.role_id ===
                                    EXECUTIVE && (
                                    <Row
                                      span={8}
                                      style={{ textAlign: 'right' }}
                                    >
                                      <Text
                                        type="secondary"
                                        className={styles.taskCount}
                                      >
                                        <ClockCircleOutlined /> {` `}
                                        {taskHours} hrs
                                      </Text>
                                    </Row>
                                  )}
                                  {isAdHOc && (
                                    <Tag
                                      color="purple"
                                      className="!m-0"
                                      bordered={false}
                                    >
                                      Ad-hoc
                                    </Tag>
                                  )}
                                  <Tag
                                    bordered={false}
                                    className="!m-0"
                                    color={
                                      item?.task_status === 'assigned'
                                        ? 'orange'
                                        : 'green'
                                    }
                                  >
                                    {item?.task_status === 'assigned'
                                      ? 'In Progress'
                                      : 'Completed'}
                                  </Tag>
                                </div>
                              </div>
                              <div className="px-3">
                                <Text
                                  type="secondary"
                                  className={styles.descriptionText}
                                >
                                  {item?.task?.description}
                                </Text>
                              </div>
                              <div className="flex items-center justify-between gap-6 px-3">
                                <div>
                                  <Text
                                    type="secondary"
                                    className={styles.secondaryText}
                                  >
                                    {item?.task?.client?.client_name} ·{' '}
                                    {item?.task?.project?.project} ·{' '}
                                    {`${item.assigner?.first_name} ${item.assigner?.last_name}`}{' '}
                                    · {`T${item.task_id}`}
                                  </Text>
                                </div>
                                {item?.task?.priority ? (
                                  <div className="flex items-center gap-2">
                                    <FlagFilled className="text-[#ff4d4f]" />
                                    <Text
                                      type="secondary"
                                      className={styles.secondaryText}
                                      style={{ fontWeight: '600' }}
                                    >
                                      Priority
                                    </Text>
                                  </div>
                                ) : null}
                              </div>
                              <div className="mt-3 pb-6 px-3 flex  justify-between gap-2 items-center">
                                {/* chat  gtp approach */}
                                <div className="flex gap-2">
                                  {userId !== item.user_id && (
                                    <CustomButton
                                      className={styles.watchButton}
                                      size="small"
                                      disabled={!isCurrentDateToday}
                                      onClick={() =>
                                        handleWatch(
                                          item.task.id,
                                          item.task.task_watchers?.some(
                                            (task) => task.user_id === userId
                                          )
                                        )
                                      }
                                      icon={
                                        item.task.task_watchers?.some(
                                          (task) => task.user_id === userId
                                        ) ? (
                                          <EyeFilled />
                                        ) : (
                                          <EyeOutlined />
                                        )
                                      }
                                    >
                                      {item.task.task_watchers?.some(
                                        (task) => task.user_id === userId
                                      )
                                        ? 'Watching'
                                        : 'Watch'}
                                    </CustomButton>
                                  )}

                                  {item?.task_status === 'assigned' &&
                                    [MANAGER, EXECUTIVE].includes(
                                      user?.user_details?.role_id
                                    ) && (
                                      <CustomButton
                                        className={styles.releaseButton}
                                        size="small"
                                        disabled={!isCurrentDateToday}
                                        onClick={() => handleRelease(item?.id)}
                                        icon={<RollbackOutlined />}
                                        loading={
                                          idToReleaseLoading === item?.id
                                        }
                                      >
                                        Release
                                      </CustomButton>
                                    )}
                                </div>
                                <div className="pt-[1px]">
                                  {item?.task?.task_watchers?.length > 0 && (
                                    <Tooltip
                                      placement="left"
                                      open={openWatch}
                                      onOpenChange={setOpenWatch}
                                      title={
                                        <div className="flex gap-2">
                                          {item?.task?.task_watchers?.length ? (
                                            <>
                                              {item.task.task_watchers
                                                .slice(0, 3)
                                                .map((watcher) => {
                                                  const {
                                                    first_name = '',
                                                    last_name = ''
                                                  } = watcher.user ?? {};
                                                  const initials =
                                                    `${first_name.charAt(
                                                      0
                                                    )}${last_name.charAt(
                                                      0
                                                    )}`.toUpperCase();
                                                  const fullName =
                                                    `${first_name} ${last_name}`.trim();
                                                  const seed =
                                                    `${first_name.charAt(
                                                      0
                                                    )}${last_name.charAt(
                                                      0
                                                    )}`.toUpperCase();

                                                  return (
                                                    <Tooltip
                                                      key={watcher.user_id}
                                                      placement="bottom"
                                                      title={fullName}
                                                    >
                                                      <Avatar
                                                        size="small"
                                                        style={{
                                                          backgroundColor:
                                                            colorHasher.hex(
                                                              seed
                                                            ),
                                                          cursor: 'pointer'
                                                        }}
                                                      >
                                                        {initials}
                                                      </Avatar>
                                                    </Tooltip>
                                                  );
                                                })}

                                              {item.task.task_watchers.length >
                                                3 && (
                                                <Tooltip
                                                  placement="bottom"
                                                  title={
                                                    <div>
                                                      {item.task.task_watchers
                                                        .slice(3)
                                                        .map((w, i) => (
                                                          <div key={i}>
                                                            {`${
                                                              w.user
                                                                ?.first_name ??
                                                              ''
                                                            } ${
                                                              w.user
                                                                ?.last_name ??
                                                              ''
                                                            }`}
                                                          </div>
                                                        ))}
                                                    </div>
                                                  }
                                                >
                                                  <span
                                                    style={{
                                                      marginLeft: '2px',
                                                      fontSize: '12px',
                                                      fontWeight: '500',
                                                      color: 'rgba(0,0,0,0.85)',
                                                      cursor: 'pointer'
                                                    }}
                                                  >
                                                    +
                                                    {item.task.task_watchers
                                                      .length - 3}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </>
                                          ) : (
                                            <span className="text-xs text-gray-400">
                                              No watchers
                                            </span>
                                          )}
                                        </div>
                                      }
                                    >
                                      {/* <EyeOutlined className="text-[#3a2aec]" /> */}
                                      <Badge
                                        count={item.task.task_watchers.length}
                                        size="small"
                                        overflowCount={99}
                                        offset={[0, 5]}
                                      >
                                        <EyeOutlined
                                          className="text-[#3a2aec]"
                                          style={{ fontSize: '18px' }}
                                        />
                                      </Badge>
                                    </Tooltip>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </Card>
                  );
                }
              )
            : ''}

          {/* grouped by user focus */}
          {Object.keys(groupedByUserFocus).length > 0
            ? Object.entries(groupedByUserFocus).map(
                ([key, { userName, tasks, index }]) => {
                  return (
                    <Card
                      className={`${styles.taskCard} mb-6`}
                      bordered={false}
                      key={index}
                    >
                      <Row align="middle" style={{ marginBottom: '16px' }}>
                        <Col span={20}>
                          <Space>
                            <Avatar
                              style={{
                                backgroundColor: colorHash.hex(
                                  userName
                                    .split(' ')
                                    .map((word) => word[0])
                                    .join('')
                                )
                              }}
                            >
                              {userName
                                .split(' ')
                                .map((word) => word[0])
                                .join('')}
                            </Avatar>
                            <Text strong>{userName}</Text>
                          </Space>
                        </Col>
                        <Col span={4} style={{ textAlign: 'right' }}>
                          <Space
                            align="center"
                            size={4}
                            className={styles.caret}
                          >
                            <Badge
                              count={tasks?.length}
                              className={styles.BadgeIconStyle}
                            />
                            {!focusTasksVisible.includes(key) ? (
                              <DownOutlined
                                onClick={() => toggleFocusTasks(key)}
                              />
                            ) : (
                              <UpOutlined
                                onClick={() => toggleFocusTasks(key)}
                              />
                            )}
                          </Space>
                        </Col>
                      </Row>
                      {focusTasksVisible.includes(key) &&
                        tasks?.map((item, index) => {
                          const isAdHOc = item.task.task_type === 'ad_hoc';
                          return (
                            <div
                              className={'!mt-1.5 bg-[#f9fafb] !rounded-lg'}
                              key={index}
                            >
                              <div className="flex items-center justify-between gap-6 px-3 pt-2.5">
                                <Title
                                  level={5}
                                  className={styles.taskTitle}
                                  style={{ margin: 0 }}
                                >
                                  {item?.task?.title}
                                </Title>
                                <div className="flex items-center gap-2">
                                  {isAdHOc && (
                                    <Tag
                                      color="purple"
                                      className="!m-0"
                                      bordered={false}
                                    >
                                      Ad-hoc
                                    </Tag>
                                  )}
                                  <Tag
                                    bordered={false}
                                    className="!m-0"
                                    color={
                                      item?.task_status === 'assigned'
                                        ? 'orange'
                                        : 'green'
                                    }
                                  >
                                    {item?.task_status === 'assigned'
                                      ? 'In Progress'
                                      : 'Completed'}
                                  </Tag>
                                </div>
                              </div>
                              <div className="px-3">
                                <Text
                                  type="secondary"
                                  className={styles.descriptionText}
                                >
                                  {item?.task?.description}
                                </Text>
                              </div>
                              <div className="flex items-center justify-between gap-6 px-3">
                                <div>
                                  <Text
                                    type="secondary"
                                    className={styles.secondaryText}
                                  >
                                    {item?.task?.client?.client_name} ·{' '}
                                    {item?.task?.project?.project} ·{' '}
                                    {`${item.assigner?.first_name} ${item.assigner?.last_name}`}{' '}
                                    · {`T${item.task_id}`}
                                  </Text>
                                </div>
                                {item?.task?.priority ? (
                                  <div className="flex items-center gap-2">
                                    <FlagFilled className="text-[#ff4d4f]" />
                                    <Text
                                      type="secondary"
                                      className={styles.secondaryText}
                                      style={{ fontWeight: '600' }}
                                    >
                                      Priority
                                    </Text>
                                  </div>
                                ) : null}
                              </div>

                              <div className="mt-3 pb-6 px-3 flex justify-between gap-2 items-center">
                                {/* chat  gtp approach */}
                                <div className="flex gap-2">
                                  {userId !== item.user_id && (
                                    <CustomButton
                                      className={styles.watchButton}
                                      size="small"
                                      disabled={!isCurrentDateToday}
                                      onClick={() =>
                                        handleWatch(
                                          item.task.id,
                                          item.task.task_watchers?.some(
                                            (task) => task.user_id === userId
                                          )
                                        )
                                      }
                                      icon={
                                        item.task.task_watchers?.some(
                                          (task) => task.user_id === userId
                                        ) ? (
                                          <EyeFilled />
                                        ) : (
                                          <EyeOutlined />
                                        )
                                      }
                                    >
                                      {item.task.task_watchers?.some(
                                        (task) => task.user_id === userId
                                      )
                                        ? 'Watching'
                                        : 'Watch'}
                                    </CustomButton>
                                  )}

                                  {item?.task_status === 'assigned' &&
                                    [MANAGER, EXECUTIVE].includes(
                                      user?.user_details?.role_id
                                    ) && (
                                      <CustomButton
                                        className={styles.releaseButton}
                                        size="small"
                                        disabled={!isCurrentDateToday}
                                        onClick={() => handleRelease(item?.id)}
                                        icon={<RollbackOutlined />}
                                        loading={
                                          idToReleaseLoading === item?.id
                                        }
                                      >
                                        Release
                                      </CustomButton>
                                    )}
                                </div>
                                <div className="pt-[1px]">
                                  {item?.task?.task_watchers?.length > 0 && (
                                    <Tooltip
                                      placement="left"
                                      open={openWatch}
                                      onOpenChange={setOpenWatch}
                                      title={
                                        <div className="flex gap-2">
                                          {item?.task?.task_watchers?.length ? (
                                            <>
                                              {item.task.task_watchers
                                                .slice(0, 3)
                                                .map((watcher) => {
                                                  const {
                                                    first_name = '',
                                                    last_name = ''
                                                  } = watcher.user ?? {};
                                                  const initials =
                                                    `${first_name.charAt(
                                                      0
                                                    )}${last_name.charAt(
                                                      0
                                                    )}`.toUpperCase();
                                                  const fullName =
                                                    `${first_name} ${last_name}`.trim();
                                                  const seed =
                                                    `${first_name.charAt(
                                                      0
                                                    )}${last_name.charAt(
                                                      0
                                                    )}`.toUpperCase();

                                                  return (
                                                    <Tooltip
                                                      key={watcher.user_id}
                                                      placement="bottom"
                                                      title={fullName}
                                                    >
                                                      <Avatar
                                                        size="small"
                                                        style={{
                                                          backgroundColor:
                                                            colorHasher.hex(
                                                              seed
                                                            ),
                                                          cursor: 'pointer'
                                                        }}
                                                      >
                                                        {initials}
                                                      </Avatar>
                                                    </Tooltip>
                                                  );
                                                })}

                                              {item.task.task_watchers.length >
                                                3 && (
                                                <Tooltip
                                                  placement="bottom"
                                                  title={
                                                    <div>
                                                      {item.task.task_watchers
                                                        .slice(3)
                                                        .map((w, i) => (
                                                          <div key={i}>
                                                            {`${
                                                              w.user
                                                                ?.first_name ??
                                                              ''
                                                            } ${
                                                              w.user
                                                                ?.last_name ??
                                                              ''
                                                            }`}
                                                          </div>
                                                        ))}
                                                    </div>
                                                  }
                                                >
                                                  <span
                                                    style={{
                                                      marginLeft: '2px',
                                                      fontSize: '12px',
                                                      fontWeight: '500',
                                                      color: 'rgba(0,0,0,0.85)',
                                                      cursor: 'pointer'
                                                    }}
                                                  >
                                                    +
                                                    {item.task.task_watchers
                                                      .length - 3}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </>
                                          ) : (
                                            <span className="text-xs text-gray-400">
                                              No watchers
                                            </span>
                                          )}
                                        </div>
                                      }
                                    >
                                      {/* <EyeOutlined className="text-[#3a2aec]" /> */}
                                      <Badge
                                        count={item.task.task_watchers.length}
                                        size="small"
                                        overflowCount={99}
                                        offset={[0, 5]}
                                      >
                                        <EyeOutlined
                                          className="text-[#3a2aec]"
                                          style={{ fontSize: '18px' }}
                                        />
                                      </Badge>
                                    </Tooltip>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </Card>
                  );
                }
              )
            : ''}

          {/*  group by user released*/}
          {/* grouped by user released - Add this section after groupedByUserFocus */}
          {Object.keys(groupedByUserReleased).length > 0
            ? Object.entries(groupedByUserReleased).map(
                ([key, { userName, tasks, index }]) => {
                  return (
                    <Card
                      className={`${styles.taskCard} mb-6`}
                      bordered={false}
                      key={index}
                    >
                      <Row align="middle" style={{ marginBottom: '16px' }}>
                        <Col span={20}>
                          <Space>
                            <Avatar
                              style={{
                                backgroundColor: colorHash.hex(
                                  userName
                                    .split(' ')
                                    .map((word) => word[0])
                                    .join('')
                                )
                              }}
                            >
                              {userName
                                .split(' ')
                                .map((word) => word[0])
                                .join('')}
                            </Avatar>
                            <Text strong>{userName}</Text>
                          </Space>
                        </Col>
                        <Col span={4} style={{ textAlign: 'right' }}>
                          <Space
                            align="center"
                            size={4}
                            className={styles.caret}
                          >
                            <Badge
                              count={tasks?.length}
                              className={styles.BadgeIconStyle}
                            />
                            {!releasedTasksVisible.includes(key) ? (
                              <DownOutlined
                                onClick={() => toggleReleasedTasks(key)}
                              />
                            ) : (
                              <UpOutlined
                                onClick={() => toggleReleasedTasks(key)}
                              />
                            )}
                          </Space>
                        </Col>
                      </Row>
                      {releasedTasksVisible.includes(key) &&
                        tasks?.map((item, index) => {
                          // Same task rendering logic as focus tasks
                          const isAdHOc = item.task.task_type === 'ad_hoc';
                          return (
                            <div
                              className={'!mt-1.5 bg-[#f9fafb] !rounded-lg'}
                              key={index}
                            >
                              <div className="flex items-center justify-between gap-6 px-3 pt-2.5">
                                <Title
                                  level={5}
                                  className={styles.taskTitle}
                                  style={{ margin: 0 }}
                                >
                                  {item?.task?.title}
                                </Title>
                                <div className="flex items-center gap-2">
                                  {isAdHOc && (
                                    <Tag
                                      color="purple"
                                      className="!m-0"
                                      bordered={false}
                                    >
                                      Ad-hoc
                                    </Tag>
                                  )}

                                  {/* {item?.task?.is_released ? ( */}
                                  <Tag
                                    bordered={false}
                                    className="!m-0"
                                    color="cyan"
                                  >
                                    Released
                                  </Tag>
                                  {/* // ) : null} */}
                                </div>
                              </div>
                              <div className="px-3">
                                <Text
                                  type="secondary"
                                  className={styles.descriptionText}
                                >
                                  {item?.task?.description}
                                </Text>
                              </div>
                              <div className="flex items-center justify-between gap-6 px-3">
                                <div>
                                  <Text
                                    type="secondary"
                                    className={styles.secondaryText}
                                  >
                                    {item?.task?.client?.client_name} ·{' '}
                                    {item?.task?.project?.project} ·{' '}
                                    {`${item.assigner?.first_name} ${item.assigner?.last_name}`}{' '}
                                    · {`T${item.task_id}`}
                                  </Text>
                                </div>
                                {item?.task?.priority ? (
                                  <div className="flex items-center gap-2">
                                    <FlagFilled className="text-[#ff4d4f]" />
                                    <Text
                                      type="secondary"
                                      className={styles.secondaryText}
                                      style={{ fontWeight: '600' }}
                                    >
                                      Priority
                                    </Text>
                                  </div>
                                ) : null}
                              </div>

                              <div className="mt-3 pb-6 px-3 flex  justify-between gap-2 items-center">
                                <div className="flex gap-2">
                                  {item?.task_status === 'assigned' &&
                                    [MANAGER, EXECUTIVE].includes(
                                      user?.user_details?.role_id
                                    ) && (
                                      <CustomButton
                                        className={styles.releaseButton}
                                        size="small"
                                        disabled={!isCurrentDateToday}
                                        onClick={() => handleRelease(item?.id)}
                                        icon={<RollbackOutlined />}
                                        loading={
                                          idToReleaseLoading === item?.id
                                        }
                                      >
                                        Release
                                      </CustomButton>
                                    )}
                                </div>
                                <div className="pt-[1px]">
                                  {item?.task?.task_watchers?.length > 0 && (
                                    <Tooltip
                                      placement="left"
                                      open={openWatch}
                                      onOpenChange={setOpenWatch}
                                      title={
                                        <div className="flex gap-2">
                                          {item?.task?.task_watchers?.length ? (
                                            <>
                                              {item.task.task_watchers
                                                .slice(0, 3)
                                                .map((watcher) => {
                                                  const {
                                                    first_name = '',
                                                    last_name = ''
                                                  } = watcher.user ?? {};
                                                  const initials =
                                                    `${first_name.charAt(
                                                      0
                                                    )}${last_name.charAt(
                                                      0
                                                    )}`.toUpperCase();
                                                  const fullName =
                                                    `${first_name} ${last_name}`.trim();
                                                  const seed =
                                                    `${first_name.charAt(
                                                      0
                                                    )}${last_name.charAt(
                                                      0
                                                    )}`.toUpperCase();

                                                  return (
                                                    <Tooltip
                                                      key={watcher.user_id}
                                                      placement="bottom"
                                                      title={fullName}
                                                    >
                                                      <Avatar
                                                        size="small"
                                                        style={{
                                                          backgroundColor:
                                                            colorHasher.hex(
                                                              seed
                                                            ),
                                                          cursor: 'pointer'
                                                        }}
                                                      >
                                                        {initials}
                                                      </Avatar>
                                                    </Tooltip>
                                                  );
                                                })}

                                              {item.task.task_watchers.length >
                                                3 && (
                                                <Tooltip
                                                  placement="bottom"
                                                  title={
                                                    <div>
                                                      {item.task.task_watchers
                                                        .slice(3)
                                                        .map((w, i) => (
                                                          <div key={i}>
                                                            {`${
                                                              w.user
                                                                ?.first_name ??
                                                              ''
                                                            } ${
                                                              w.user
                                                                ?.last_name ??
                                                              ''
                                                            }`}
                                                          </div>
                                                        ))}
                                                    </div>
                                                  }
                                                >
                                                  <span
                                                    style={{
                                                      marginLeft: '2px',
                                                      fontSize: '12px',
                                                      fontWeight: '500',
                                                      color: 'rgba(0,0,0,0.85)',
                                                      cursor: 'pointer'
                                                    }}
                                                  >
                                                    +
                                                    {item.task.task_watchers
                                                      .length - 3}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </>
                                          ) : (
                                            <span className="text-xs text-gray-400">
                                              No watchers
                                            </span>
                                          )}
                                        </div>
                                      }
                                    >
                                      {/* <EyeOutlined className="text-[#3a2aec]" /> */}
                                      <Badge
                                        count={item.task.task_watchers.length}
                                        size="small"
                                        overflowCount={99}
                                        offset={[0, 5]}
                                      >
                                        <EyeOutlined
                                          className="text-[#3a2aec]"
                                          style={{ fontSize: '18px' }}
                                        />
                                      </Badge>
                                    </Tooltip>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </Card>
                  );
                }
              )
            : ''}
          {/** end of group by of released */}

          {/* {!Object.keys(groupedByUser).length &&
          !Object.keys(groupedByUserFocus).length &&
          !escalatedTasks.length ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No plan published for today"
              className="sm:-translate-x-1/2 sm:fixed sm:-translate-y-1/2 top-1/2 left-1/2"
            />
          ) : null} */}

          {/* {!Object.keys(groupedByUser).length &&
          !Object.keys(groupedByUserFocus).length &&
          !Object.keys(groupedByUserReleased).length && // Add this
          !escalatedTasks.length ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No plan published for today"
              className="sm:-translate-x-1/2 sm:fixed sm:-translate-y-1/2 top-1/2 left-1/2"
            />
          ) : null} */}

          {(() => {
            const hasEscalatedTasks = escalatedTasks.length > 0;
            const hasRegularTasks = Object.keys(groupedByUser).length > 0;
            const hasFocusTasks = Object.keys(groupedByUserFocus).length > 0;
            const hasReleasedTasks =
              Object.keys(groupedByUserReleased).length > 0;

            let shouldShowEmpty = false;
            let emptyMessage = 'No tasks found';

            if (showReleasedOnly) {
              // For released filter: show empty if no released tasks (ignore escalated tasks for this filter)
              shouldShowEmpty = !hasReleasedTasks;
              emptyMessage = 'No released tasks found';
            } else if (showMyFocus) {
              // For focus filter: show empty if no focus tasks (ignore escalated tasks for this filter)
              shouldShowEmpty = !hasFocusTasks;
              emptyMessage = 'No focus tasks found';
            } else if (showOnlyPendingTask) {
              // For pending filter: show empty if no regular tasks and no escalated tasks
              shouldShowEmpty = !hasRegularTasks && !hasEscalatedTasks;
              emptyMessage = 'No pending tasks found';
            } else {
              // Default view: show empty if no regular tasks and no escalated tasks
              shouldShowEmpty = !hasRegularTasks && !hasEscalatedTasks;
              emptyMessage = 'No tasks found for today';
            }

            return shouldShowEmpty ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={emptyMessage}
                className="sm:-translate-x-1/2 sm:fixed sm:-translate-y-1/2 top-1/2 left-1/2"
              />
            ) : null;
          })()}
        </Col>
      </Row>

      {[MANAGER, EXECUTIVE].includes(user?.user_details?.role_id) ? (
        <FloatingActionButton handleGetAllTasks={handleGetAllTasks} />
      ) : (
        ''
      )}
      <style>
        {`
        .ant-tooltip-inner{
        font-size:12px;
        background-color:#fff;
        color:rgba(0,0,0,0.75);
        }
        .ant-tooltip-arrow-content {
  --antd-arrow-background-color: linear-gradient(to right bottom, #fff, #fff);
}
          .ant-avatar-sm{
          width:21px;
          height:21px;
          }
          .ant-avatar-string{
          margin-top:-1px;
          }
          .ant-badge-count-sm{
            min-width: 13px;
             height: 13px;
            font-size: 9px;
          }
          .ant-badge-count, .ant-badge-dot, .ant-badge .ant-scroll-number-custom-component{
          top:-2px;
          }
          .ant-scroll-number-only{
          vertical-align: super;
          }
        `}
      </style>
    </>
  );
};

export default TaskTracker;
